/**
 * @module app.shared
 */

import { inject, bindingMode, processContent, TaskQueue, computedFrom, Disposable, BindingEngine } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { I18N } from 'aurelia-i18n';
import { moveRulesToHelperSlot } from 'app/shared/elements/ess-rule';
import { fireEvent } from 'app/shared/lib/fire-events';
import { getKey, Key, getChar } from 'app/shared/lib/keys';
import { MatcherFunction, modelIdMatcher } from 'app/shared/lib/matcher';
import { resolveProperty } from 'app/shared/lib/objects'
import { debounce, task } from 'app/shared/lib/decorators';
import { TickService, TickHandler, ITickEvent } from 'app/shared/services/event-services';
import { selectText, clearSelection } from 'app/shared/lib/dom';
import { IInput } from 'app/shared/elements/forms/input-base';
import { guid } from 'app/shared/lib/guid';

const SEARCH_TIMEOUT_MS = 1500;
const RESET_TIMEOUT_MS = 500;

type optionSelectedFunction = (option?: any) => void;


export enum SelectState {
    Open = 1,
    Closed = 2
}

@inject(Element, I18N, TaskQueue, TickService, BindingEngine)
@processContent(moveRulesToHelperSlot)
export class EssSelectCustomElement<T> implements IInput {

    protected id: guid = guid();

    @bindable.any({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'syncValueAndOptions' })
    public value: T | T[] | null = null;
    protected valueIsPopulatedArray: boolean = false;

    @bindable.string()
    public testName: string = '';

    @bindable.array({ changeHandler: 'syncValueAndOptions' })
    public options: T[] = [];

    @bindable.function
    public optionSelected: optionSelectedFunction;

    @bindable.string()
    protected icon: string = '';

    @bindable.boolean
    public multiple: boolean = false;

    @bindable.boolean
    public allowSelectAll: boolean = true;

    @bindable.booleanAttr
    public readonly: boolean = false;

    @bindable.boolean
    public disabled: boolean = false;

    @bindable.boolean
    public required: boolean = false;

    @bindable.stringNull
    public label: string | null = null;

    @bindable.stringNull
    public requiredValidatorLabel: string | null = null;

    @bindable.stringNull
    public placeholder: string | null = null;

    @bindable.stringNull
    public autocomplete: string = 'off';

    @bindable.number
    public maxDisplay: number = 10;

    @bindable.string
    public displayProperty: string = '$display';

    @bindable.stringNull()
    protected labelledbyId: string | null = null;

    // INFO [Matt] Set in the constructor to preserve public / private member order.
    @bindable.function
    public matcher: MatcherFunction;

    @bindable.stringNull
    public arialabelValue: string | null = null;

    public state: SelectState = SelectState.Closed;

    protected search = '';
    protected hovered: number = -1;
    protected ignoreMouse: boolean = false;
    protected SelectState = SelectState;
    protected resetTimeout = Infinity;
    protected activeDescendantId: string | null = null;

    protected defaultMatcher = modelIdMatcher;

    protected defaultOptionSelected: optionSelectedFunction = (option: any) => { }

    protected $inputText!: HTMLSpanElement;
    protected $options!: HTMLElement;
    protected $isHovered!: boolean;

    constructor(protected readonly element: HTMLElement,
        protected readonly i18n: I18N,
        protected readonly queue: TaskQueue,
        protected readonly ticker: TickService,
        protected readonly bindingEngine: BindingEngine) {
        this.matcher = this.defaultMatcher;
        this.optionSelected = this.defaultOptionSelected;
    }

    public select(item: T | null): void;
    public select(index: number): void;
    public select(item: T | number | null) {
        this.value = this.normalizeItemOrIndex(item);
        this.optionSelected();
    }

    public toggle(item: T | null, include?: boolean): boolean;
    public toggle(index: number, include?: boolean): boolean;
    public toggle(item: T | number | null, include?: boolean): boolean {
        item = this.normalizeItemOrIndex(item);
        if(item === null) {
            return false;
        }
        if(this.multiple) {
            const value = this.value as T[] || [];
            const index = value.findIndex((i) => this.matcher(i, item));
            if(include === undefined) {
                include = index === -1;
            }
            if(include === true) {
                value.push(item);
            } else {
                value.splice(index, 1);
            }
            this.value = value;
            this.optionSelected();
        } else {
            if(include === undefined) {
                include = item !== this.value;
            }
            this.value = include ? item : null;
        }
        return include;
    }

    protected handleDocumentMousemove = (event: MouseEvent) => {
        if(this.ignoreMouse) {
            // movement detected, resume normal mouse operation
            this.ignoreMouse = false;
            document.removeEventListener('mousemove', this.handleDocumentMousemove);
        }
    }

    public open() {
        if(this.disabled) {
            return;
        }

        this.state = SelectState.Open;
        this.updateDropdownPosition();
        if(this.multiple === false && this.value !== null) {
            this.hovered = this.options.indexOf(this.value as T);
        }
        else if(this.multiple && this.value !== null) {
            const value = this.value as T[];
            // Hover the first selected option, if it exists
            if(value.length > 0) {
                const minIndex = value.reduce<number | null>((minIndex: number | null, value) => {
                    const index = this.options.findIndex((i) => this.matcher(i, value));
                    return !!minIndex ? Math.min(minIndex, index) : index;
                }, null);
                if(minIndex) {
                    this.hoverIndex(minIndex);
                }
            }
        }
    }

    public close() {
        this.resetSearch();
        this.state = SelectState.Closed;
    }

    protected hoverIndex(index: number, fromMouse: boolean = false) {
        // Ignore mouseenter while opening dropdown
        if(this.ignoreMouse && fromMouse) return;

        this.hovered = index;
        if(!this.multiple || (this.multiple && this.allowSelectAll)) {
            // Add an offset when the Placeholder is visible
            // INFO[Watt]: Keep the condition in sync with the view side logic
            this.$options.children[index + 1].scrollIntoView({ block: 'nearest' });
        }
        else {
            this.$options.children[index]?.scrollIntoView({ block: 'nearest' });
        }
    }

    protected toggleOrSelectAndClose(item: T | number | null) {
        item = this.normalizeItemOrIndex(item);
        if(this.multiple === true) {
            this.toggle(item);
        } else {
            this.select(item);
            this.close();
        }
        if(this.required && this.multiple && Array.isArray(this.value)) {
            this.valueIsPopulatedArray = this.value.length > 0;
        }
        this.fireChangeEvent();
    }

    protected deselect(item: T | number | null, include?: boolean) {
        item = this.normalizeItemOrIndex(item);
        if(this.multiple) {
            const value = this.value as T[] || [];
            const index = value.findIndex((i) => this.matcher(i, item));
            if(index > -1) {
                value.splice(index, 1);
            }
            this.value = value;
            this.optionSelected();
        }
        else {
            this.select(-1)
            this.close();
        }
    }

    protected clearSelect() {
        if(this.multiple && Array.isArray(this.value)) {
            this.value.splice(0);
        }
        else {
            this.select(-1);
        }
    }

    // INFO [Matt] This is the internal handler for the select all option.
    protected doSelectAll(include: boolean) {
        const value = this.value as T[] || [];
        value.splice(0);
        if(include) { value.push(...this.options); }
        this.value = value;
        if(this.required && this.multiple && Array.isArray(this.value)) {
            this.valueIsPopulatedArray = this.value.length > 0;
        }
        this.fireChangeEvent();
    }

    protected normalizeItemOrIndex(index: number | T | null): T | null {
        const isIndex = typeof (index) === 'number' && index < this.options.length && !this.options.includes(index as T);
        return isIndex ? this.options[index as number] : index as T;
    }

    @debounce() // INFO [Matt] Prevent multiple calls from value and options changed callbacks.
    @task // INFO [Matt] Queue a task to allow bindings to catch up.
    protected syncValueAndOptions() {
        const { value: val, options, matcher } = this;
        const valueIndex = this.options.findIndex((i) => this.matcher(i, val));
        if(val === null) {
            this.activeDescendantId = null;
            return;
        }
        const match = options.find((option) => matcher(val, option)) || null;
        // INFO [Matt] If we don't find a match, we don't want to sync the value,
        // rather we want (a) the select "input" to display the current value
        // even though it is not in the list and (b) for the value to remain
        // the same.
        if(match) {
            this.value = match;
        }
        if(!this.multiple && valueIndex > -1) {
            this.activeDescendantId = this.id + '-option-' + valueIndex;
        } else { this.activeDescendantId = null; }

        if(this.required && this.multiple && Array.isArray(this.value)) {
            this.valueIsPopulatedArray = this.value.length > 0;
        }
    }

    protected updateDropdownPosition() {
        if(this.state === SelectState.Open) {
            const { y, left, width, top, bottom } = this.element.getBoundingClientRect();
            const opensUp = y > window.innerHeight / 2;
            this.$options.style.setProperty('left', `${left}px`);

            // INFO [Matt] I can't account for them, but there is a 12px offset
            // on the top and a 5px offset on the bottom that we have to add back in.
            this.$options.style.setProperty('top', opensUp ? '' : `${top + 12}px`);
            this.$options.style.setProperty('bottom', opensUp ? `calc(100vh - ${bottom - 5}px)` : '');

            this.$options.style.setProperty('width', `${width}px`);
        }
    };

    protected matcherChanged(matcher: MatcherFunction | null) {
        if(matcher === null) {
            this.matcher = this.defaultMatcher;
        }
        this.syncValueAndOptions();
    }

    protected fireChangeEvent() {
        this.queue.queueTask(() => fireEvent(this.element, 'change'));
    }

    protected resetSearch() {
        this.search = '';
        this.resetTimeout = Infinity;
    }

    protected updateSearch(char: string) {
        const search = this.search + char;
        const re = new RegExp(`^${search}`, 'i');
        const index = this.options.findIndex((option: any) => re.test(resolveProperty(option, this.displayProperty)));
        if(index > -1) {
            this.search = search;
            this.resetTimeout = Date.now() + SEARCH_TIMEOUT_MS;
            this.hoverIndex(index);
        } else {
            this.resetTimeout = Date.now() + RESET_TIMEOUT_MS;
        }
    }

    @computedFrom('label', 'requiredValidatorLabel')
    protected get requiredValueMessage() {
        const message = this.requiredValidatorLabel || this.i18n.tr(`ui:validation:${this.multiple ? 'oneRequired' : 'required'}`, { what: this.i18n.tr(this.label || 'ui:labels.value') });
        return message;
    }

    @computedFrom('value.length')
    protected get valueCount() {
        return Array.isArray(this.value) ? this.value.length : undefined;
    }

    // Determines if the provided option is a selected option
    // INFO: When calling this function in .bind expressions in the HTML, value.length
    // must be provided as a third parameter so that Aurelia calls valuesMatch with the
    // proper scope. (otherwise value will not be treated as an array for multi-selects)
    protected valuesMatch(value: T | T[] | null, option: T): boolean {
        if(!!this.matcher && !!value) {
            if(Array.isArray(value)) {
                return value.some(v => this.matcher(v, option));
            }
            return this.matcher(value, option);
        }
        return false;
    }

    protected attached() {
        this.ticker.addEventListener('tick', this.handleTick);
    }
    protected detached() {
        this.ticker.removeEventListener('tick', this.handleTick);
    }

    protected handleBlur({ relatedTarget: next }: FocusEvent) {
        if(!next || !this.element.contains(next as Node)) {
            this.close();
        }
        clearSelection();
    }

    protected handleFocus() {
        if(this.readonly) {
            if(!this.$isHovered) {
                selectText(this.$inputText);
            }
        }
    }

    protected handleTick: TickHandler = (event: ITickEvent) => {
        if(this.search.length > 0 && event.time > this.resetTimeout) {
            this.resetSearch();
        }
    };

    protected handleMousedown(): void | true {
        if(this.readonly || this.disabled) {
            return true;
        }
        this.$inputText.focus();
        if(this.state === SelectState.Closed) {
            this.open();
        }
    }

    protected handleKeydown(event: KeyboardEvent): boolean {
        // if keyboard navgation occurs, then ignore the mouseenter event
        if(!this.ignoreMouse) {
            // ignore the mouseenter event until movement is detected
            this.ignoreMouse = true;
            document.addEventListener('mousemove', this.handleDocumentMousemove);
        }
        // INFO [Matt] If there's a search value and the user types a space,
        // it is interpreted as part of the search value, and isn't used to
        // toggle the dropdown.
        const char = getChar(event);
        if(char !== null && (char !== ' ' || this.search.length > 0)) {
            this.updateSearch(char);
            return false;
        }
        const minIndex = this.multiple && this.allowSelectAll ? -1 : 0;
        const page = 10;
        switch(getKey(event)) {
            case Key.Up:
                if(this.state === SelectState.Closed) {
                    if(!this.readonly) {
                        this.open();
                        this.hoverIndex(Math.max(this.hovered, minIndex));
                        return false;
                    }
                    return true;
                }
                this.hoverIndex(Math.max(this.hovered - 1, minIndex));
                return false;
            case Key.Down:
                if(this.state === SelectState.Closed) {
                    if(!this.readonly) {
                        this.open();
                        this.hoverIndex(Math.max(this.hovered, minIndex));
                        return false;
                    }
                    return true;
                }
                this.hoverIndex(Math.min(this.hovered + 1, this.options.length - 1));
                return false;
            case Key.Space:
            case Key.Enter:
                if(this.state === SelectState.Closed) {
                    if(!this.readonly) {
                        this.open();
                        this.hoverIndex(this.hovered > -1 ? Math.min(this.hovered, this.options.length - 1) : -1);
                    }
                }
                else if(this.hovered >= minIndex) {
                    if(this.hovered === -1) {
                        this.queue.queueTask(() => (this.$options.children[0] as HTMLElement).click());
                    } else {
                        this.toggleOrSelectAndClose(this.hovered);
                    }
                }
                return false;
            case Key.Tab:
                if(this.state === SelectState.Open && this.hovered > minIndex && !this.multiple) {
                    this.select(this.hovered);
                }
                this.close();
                return true;
            case Key.Esc:
                if(this.state === SelectState.Open) {
                    this.close();
                    event.stopPropagation();
                }
                return false;
            case Key.Home:
                if(this.state === SelectState.Open) {
                    this.hoverIndex(minIndex);
                }
                return false;
            case Key.End:
                if(this.state === SelectState.Open) {
                    this.hoverIndex(this.options.length - 1);
                }
                return false;
            case Key.Delete:
            case Key.Backspace:
                if(this.state === SelectState.Closed) {
                    if(!this.readonly) this.clearSelect();
                }
                else {
                    this.deselect(this.hovered);
                }
                return false;
            case Key.PageUp:
                if(this.state === SelectState.Open) {
                    this.hoverIndex(Math.max(this.hovered - page, minIndex));
                    return false;
                }
                return true;
            case Key.PageDown:
                if(this.state === SelectState.Open) {
                    this.hoverIndex(Math.min(this.hovered + page, this.options.length - 1))
                    return false;
                }
                return true;
        }
        return true;

    }
}
